import {MAX_UPLOAD_BYTES, type DeckContent, type DeckRevision, type SharedDeck, type SavedAsset} from './shared-deck';

export class SaveConflict extends Error {
  constructor(readonly current: SharedDeck) {super('A newer version is online. Your changes are kept locally.');}
}

export async function sharedRequest(path: string, init?: RequestInit): Promise<Response> {
  return fetch(path, {...init, cache: 'no-store', signal: AbortSignal.timeout(init?.body instanceof Blob ? 120_000 : 12_000)});
}

/** Browser recovery and server revision are distinct; reload never rebases an unsent edit. */
export class SharedWorkspace {
  current: SharedDeck | null = null;
  connected = false;
  localOnly = false;
  pending = false;
  pendingSource: string | null = null;
  baseRevision = 0;
  assets: Record<string, SavedAsset> = {};
  baseDeckId: string;
  readonly cacheKey: string;
  readonly baseKey: string;

  constructor(readonly id: string) {
    this.baseDeckId = id;
    this.cacheKey = `slides:shared:${id}`;
    this.baseKey = `slides:save-base:${id}`;
    try {
      this.current = JSON.parse(localStorage.getItem(this.cacheKey) ?? 'null');
      const base = JSON.parse(localStorage.getItem(this.baseKey) ?? 'null');
      this.baseRevision = base?.revision ?? 0;
      this.pending = base?.pending ?? false;
      this.pendingSource = base?.source ?? null;
      this.assets = base?.assets ?? this.current?.assets ?? {};
      this.baseDeckId = base?.baseDeckId ?? this.current?.baseDeckId ?? id;
    } catch {throw new Error('Could not read saved version information. Export your local deck before clearing storage.');}
  }

  async refresh(): Promise<SharedDeck | null> {
    const response = await sharedRequest(`/api/decks/${this.id}`);
    if (!response.headers.get('content-type')?.includes('application/json')) {
      this.localOnly = ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname) && (response.ok || response.status === 404);
      if (this.localOnly) this.current = null;
      throw new Error('Online saving is unavailable here.');
    }
    if (!response.ok && response.status !== 404) throw new Error('Could not load the online version.');
    this.connected = true;
    const remote = response.status === 404 ? null : await response.json() as SharedDeck;
    if (remote) {
      this.current = remote;
      localStorage.setItem(this.cacheKey, JSON.stringify(remote));
    }
    return remote;
  }

  chooseSource(local: string | null, baseline: string, previous: SharedDeck | null): string {
    // Preserve pre-cloud browser drafts and unsent offline edits, including their
    // original revision. They must conflict, not silently overwrite newer work.
    const legacy = !localStorage.getItem(this.baseKey) && local !== null && local !== baseline;
    if (this.pending || legacy) {
      this.pending = true;
      if (legacy) this.baseRevision = previous?.revision ?? 0;
      this.persistBase();
      return this.pendingSource ?? local ?? baseline;
    }
    this.accept(this.current);
    return this.current?.source ?? local ?? baseline;
  }

  accept(remote: SharedDeck | null): void {
    this.current = remote;
    this.baseRevision = remote?.revision ?? 0;
    this.assets = remote?.assets ?? {};
    this.baseDeckId = remote?.baseDeckId ?? this.id;
    this.pending = false;
    this.pendingSource = null;
    if (remote) localStorage.setItem(this.cacheKey, JSON.stringify(remote));
    this.persistBase();
  }

  stage(source?: string): void {this.pending = true; this.pendingSource = source ?? this.pendingSource; this.persistBase();}

  private persistBase(): void {
    localStorage.setItem(this.baseKey, JSON.stringify({revision: this.baseRevision, pending: this.pending, source: this.pendingSource, assets: this.assets, baseDeckId: this.baseDeckId}));
  }

  async save(content: DeckContent, originalSource: string, id = this.id): Promise<SharedDeck> {
    const response = await sharedRequest(`/api/decks/${id}`, {
      method: 'PUT', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({...content, originalSource, baseRevision: id === this.id ? this.baseRevision : 0}),
    });
    if (!response.headers.get('content-type')?.includes('application/json')) throw new Error('Online saving is unavailable here. Your local save is kept.');
    const result = await response.json();
    if (response.status === 409) throw new SaveConflict(result.current);
    if (!response.ok) throw new Error(result.error ?? 'Could not save online.');
    if (id === this.id) {this.connected = true; this.accept(result);}
    return result;
  }

  async version(revision: number): Promise<DeckRevision> {
    const response = await sharedRequest(`/api/decks/${this.id}?revision=${revision}`);
    if (!response.ok) throw new Error('Could not load this version.');
    return response.json();
  }

  async upload(blob: Blob): Promise<string> {
    if (blob.size > MAX_UPLOAD_BYTES) throw new Error('Online media uploads are limited to 64 MB per file. Your file is kept locally.');
    const hash = [...new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()))].map(x => x.toString(16).padStart(2, '0')).join('');
    const response = await sharedRequest(`/api/deck-assets/${hash}`, {method: 'PUT', headers: {'Content-Type': blob.type}, body: blob});
    if (!response.ok) throw new Error('Could not upload media. Your file and text are kept locally.');
    return (await response.json()).url;
  }
}
